import { useEffect, useMemo, useState } from "react";
import { api } from "../api.ts";
import { Markdown, inline } from "../components/Markdown.tsx";
import { C, mono, sans } from "../theme.ts";
import {
  parseProgramme, parseCores, parseExercises, checkCues,
  type Result, type Programme, type Cores, type Exercises,
  type Tier, type Ladder, type Core, type Movement, type MovementRow,
} from "../workout/parse.ts";
import { loadWorkoutSources } from "../workout/load.ts";

// The Workout view: the four tiers, the two cores, the six ladders and the
// cueing, read from three markdown documents and rendered one movement at a
// time, for a phone held in a room between sets.
//
// ───────────────────────────────────────────────────────────────────────────
// Read this before changing anything in this file.
//
//     The system may know where you are.
//     It may never tell you how you're doing.
//
// Each ladder may carry a current rung. That is a cursor and it is allowed.
// Everything else about a rung is forbidden here: when it last changed, how
// long it has been there, how many times it has moved, a chart of it, or any
// styling that reads as *stalled* — including styling the rungs below the
// cursor as done or the rungs above it as locked. A rung is a fact about
// today with no history attached, and this file renders exactly that.
//
// This view writes nothing. Not a rung, not a tick, not an expanded row, not
// a last-opened tier, not a search. When a rung moves, Karim tells Claude and
// Claude edits programme.md through the connector; this view re-reads it.
//
// Never here: a timer · a start or complete control · a session log · a count
// of anything · a "last performed" date · a rung-change history · a streak ·
// a chart · a notification · a progress ring · a personal best · anything
// comparing one week to another. Sworkit runs the session. This names things
// and explains them.
//
// Nothing is hidden. A movement with no cue says so. A ladder with no marked
// rung says so. A parked movement is greyed with its unlock condition, not
// removed. A document that does not parse is shown as plain markdown with the
// reason, and nothing half-parsed is ever rendered.
// ───────────────────────────────────────────────────────────────────────────

interface Loaded {
  programme: Result<Programme> & { raw: string };
  cores: Result<Cores> & { raw: string };
  exercises: Result<Exercises> & { raw: string };
}

type Screen =
  | { kind: "index" }
  | { kind: "tier"; n: number; variant: 1 | 2 }
  | { kind: "core"; letter: string }
  | { kind: "ladder"; name: string }
  | { kind: "movements" }
  | { kind: "doc"; which: keyof Loaded };

/** Where to open, when arrived at from somewhere else — the Training slot's detail sheet. */
export interface WorkoutTarget { variant: 1 | 2 }

export function Workout({ target }: { target?: WorkoutTarget | null }) {
  const [docs, setDocs] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [screen, setScreen] = useState<Screen>({ kind: "index" });
  const [query, setQuery] = useState("");

  useEffect(() => {
    let live = true;
    loadWorkoutSources(api.doc)
      .then((src) => {
        if (!live) return;
        const exercises = parseExercises(src.exercises);
        let programme = parseProgramme(src.programme);
        let cores = parseCores(src.core);
        // A cue pointing at nothing is an error in the document that carries it.
        if (exercises.ok) {
          const x = checkCues(programme.ok ? programme.value : null, cores.ok ? cores.value : null, exercises.value);
          if (x.programme && programme.ok) programme = { ok: false, reason: x.programme };
          if (x.cores && cores.ok) cores = { ok: false, reason: x.cores };
        }
        setDocs({
          programme: { ...programme, raw: src.programme },
          cores: { ...cores, raw: src.core },
          exercises: { ...exercises, raw: src.exercises },
        });
      })
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, []);

  // Arriving from the Training slot: straight to the right variant. Which tier
  // is not chosen here — only Karim knows how much he has today — so it opens
  // on the default, Cheat Code, with the others one tap away.
  useEffect(() => {
    if (target && docs?.programme.ok) {
      const first = docs.programme.value.tiers[0];
      if (first) setScreen({ kind: "tier", n: first.n, variant: target.variant });
    }
  }, [target, docs]);

  if (error) return <p style={{ color: C.alert, fontSize: 13, padding: "0 18px" }}>{error}</p>;
  if (!docs) return <p style={{ color: C.inkFaint, fontSize: 13, padding: "0 18px" }}>Reading the programme…</p>;

  const movements = docs.exercises.ok ? docs.exercises.value.movements : null;
  const back = (label: string, to: Screen = { kind: "index" }) => (
    <button
      onClick={() => setScreen(to)}
      style={{
        fontFamily: mono, fontSize: 11, color: C.inkFaint, letterSpacing: 1,
        background: "none", border: "none", padding: "0 0 12px", cursor: "pointer",
      }}
    >‹ {label}</button>
  );

  // ── a whole document, when it could not be parsed ───────────────────────
  if (screen.kind === "doc") {
    const d = docs[screen.which];
    return (
      <div style={{ padding: "0 18px" }}>
        {back("WORKOUT")}
        {!d.ok && (
          <p style={{ fontSize: 13, color: C.alert, lineHeight: 1.55, margin: "0 0 14px" }}>
            This document could not be read as a programme, so it is shown as written. {d.reason}.
          </p>
        )}
        <Markdown text={d.raw} />
      </div>
    );
  }

  if (screen.kind === "tier" && docs.programme.ok) {
    const tier = docs.programme.value.tiers.find((t) => t.n === screen.n);
    if (tier) {
      return (
        <div style={{ padding: "0 18px" }}>
          {back("WORKOUT")}
          <TierScreen
            tier={tier} variant={screen.variant}
            onVariant={(v) => setScreen({ kind: "tier", n: tier.n, variant: v })}
            programme={docs.programme.value}
            cores={docs.cores.ok ? docs.cores.value : null}
            coresReason={docs.cores.ok ? null : docs.cores.reason}
            movements={movements}
            exercisesReason={docs.exercises.ok ? null : docs.exercises.reason}
          />
        </div>
      );
    }
  }

  if (screen.kind === "core" && docs.cores.ok) {
    const core = docs.cores.value.cores.find((k) => k.letter === screen.letter);
    if (core) {
      return (
        <div style={{ padding: "0 18px" }}>
          {back("WORKOUT")}
          <CoreScreen core={core} cores={docs.cores.value} movements={movements} exercisesReason={docs.exercises.ok ? null : docs.exercises.reason} />
        </div>
      );
    }
  }

  if (screen.kind === "ladder" && docs.programme.ok) {
    const ladder = docs.programme.value.ladders.find((l) => l.name === screen.name);
    if (ladder) {
      return (
        <div style={{ padding: "0 18px" }}>
          {back("WORKOUT")}
          <LadderScreen ladder={ladder} movements={movements} exercisesReason={docs.exercises.ok ? null : docs.exercises.reason} />
        </div>
      );
    }
  }

  if (screen.kind === "movements" && movements) {
    return (
      <div style={{ padding: "0 18px" }}>
        {back("WORKOUT")}
        <MovementList movements={movements} preamble={docs.exercises.ok ? docs.exercises.value.preamble : ""} />
      </div>
    );
  }

  // ── the index ───────────────────────────────────────────────────────────
  const q = query.trim().toLowerCase();
  return (
    <div style={{ padding: "0 18px" }}>
      {/* One field. It filters the cueing — names, muscles, the text of every
          block — and is the most common real use of this screen: one movement,
          mid-session, found in two taps. Nothing about it is remembered. */}
      <input
        type="search" value={query} onChange={(e) => setQuery(e.target.value)}
        placeholder={movements ? "find a movement or a muscle" : "the cueing could not be read"}
        disabled={!movements}
        style={{
          width: "100%", boxSizing: "border-box", fontFamily: sans, fontSize: 15,
          padding: "11px 14px", borderRadius: 10, border: `1px solid ${C.ruleStrong}`, background: C.card,
          marginBottom: 6,
        }}
      />

      {q && movements ? (
        <SearchResults q={q} movements={movements} />
      ) : (
        <Index docs={docs} onOpen={setScreen} />
      )}
    </div>
  );
}

// ── the index ─────────────────────────────────────────────────────────────

const reasonOf = (r: Result<unknown>) => (r.ok ? "" : r.reason);

const Label = ({ children }: { children: string }) => (
  <p style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, letterSpacing: 1, margin: "22px 0 8px" }}>
    {children}
  </p>
);

const card: React.CSSProperties = {
  background: C.card, borderRadius: 12, border: `1px solid ${C.rule}`,
  padding: "12px 14px", marginBottom: 10, cursor: "pointer", fontFamily: sans,
};

function Index({ docs, onOpen }: { docs: Loaded; onOpen: (s: Screen) => void }) {
  const [about, setAbout] = useState(false);
  const programme = docs.programme.ok ? docs.programme.value : null;
  const cores = docs.cores.ok ? docs.cores.value : null;
  const exercises = docs.exercises.ok ? docs.exercises.value : null;

  return (
    <>
      {programme ? (
        <>
          <Label>THE TIERS</Label>
          {programme.tiers.map((t, i) => (
            <div key={t.n} onClick={() => onOpen({ kind: "tier", n: t.n, variant: 1 })} style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.35 }}>{t.name}</div>
                <div style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, whiteSpace: "nowrap" }}>{t.cap}</div>
              </div>
              {t.when && (
                <div style={{ fontSize: 13, color: C.inkSoft, lineHeight: 1.5, marginTop: 5 }}>{t.when}</div>
              )}
              <Intensity position={i + 1} of={programme.tiers.length} />
            </div>
          ))}
        </>
      ) : (
        <DocFailed which="programme" reason={reasonOf(docs.programme)} onOpen={onOpen} />
      )}

      {cores ? (
        <>
          <Label>THE CORES</Label>
          {cores.cores.map((k) => (
            <div key={k.letter} onClick={() => onOpen({ kind: "core", letter: k.letter })} style={card}>
              <div style={{ fontSize: 15, lineHeight: 1.4 }}>Core {k.letter} — {k.name}</div>
              <div style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, marginTop: 4 }}>
                {k.steps.map((s) => s.movement.split(" — ")[0].split(",")[0]).join(" · ")}
              </div>
            </div>
          ))}
        </>
      ) : (
        <DocFailed which="cores" reason={reasonOf(docs.cores)} onOpen={onOpen} />
      )}

      {programme && (
        <>
          <Label>THE LADDERS</Label>
          {programme.ladders.map((l) => (
            <div key={l.name} onClick={() => onOpen({ kind: "ladder", name: l.name })} style={card}>
              <div style={{ fontSize: 15, lineHeight: 1.4 }}>{l.name}</div>
              <div style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, marginTop: 4 }}>
                {l.where}
                {/* The cursor, and only the cursor: which rung, never since
                    when — and not "2 of 4", which reads as a fraction done. */}
                {l.current !== null && ` · on rung ${l.current}`}
              </div>
            </div>
          ))}
        </>
      )}

      {exercises ? (
        <>
          <Label>THE CUEING</Label>
          <div onClick={() => onOpen({ kind: "movements" })} style={card}>
            <div style={{ fontSize: 15, lineHeight: 1.4 }}>Every movement, in order</div>
            <div style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, marginTop: 4 }}>
              target · where it is · right · wrong · avoid
            </div>
          </div>
        </>
      ) : (
        <DocFailed which="exercises" reason={reasonOf(docs.exercises)} onOpen={onOpen} />
      )}

      {/* The prose around the tables — why tiers, the two axes, what is still
          open, the superseded rotation. Not the thing you open between sets,
          so it is folded, but it is here: nothing in the documents is hidden. */}
      {programme && (
        <>
          <Label>ABOUT THE PROGRAMME</Label>
          <div onClick={() => setAbout((x) => !x)} style={{ ...card, cursor: "pointer" }}>
            <div style={{ fontSize: 14, color: C.inkSoft }}>{about ? "The programme's own words, in full" : "The programme's own words"}</div>
            {about && (
              <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 12, cursor: "auto" }}>
                <Markdown text={programme.preamble} />
                <Markdown text={programme.tiersIntro} />
                <Markdown text={programme.laddersIntro} />
                <Markdown text={programme.after} />
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}

/**
 * Four marks, the first n filled. It encodes where a tier sits among the four
 * and nothing else: not effort, not difficulty, not a score, not anything about
 * how often it is chosen. Cheat Code shows one mark because it is first, and
 * the document says in its own words that first is not bottom.
 */
function Intensity({ position, of }: { position: number; of: number }) {
  return (
    <div style={{ display: "flex", gap: 4, marginTop: 10 }} aria-label={`tier ${position} of ${of}`}>
      {Array.from({ length: of }, (_, i) => (
        <span key={i} style={{
          width: 18, height: 3, borderRadius: 2,
          background: i < position ? C.inkFaint : C.rule,
        }} />
      ))}
    </div>
  );
}

function DocFailed({ which, reason, onOpen }: { which: keyof Loaded; reason: string; onOpen: (s: Screen) => void }) {
  const name = which === "programme" ? "programme.md" : which === "cores" ? "core.md" : "exercises.md";
  return (
    <>
      <Label>{name.toUpperCase()}</Label>
      <div onClick={() => onOpen({ kind: "doc", which })} style={{ ...card, borderLeft: `4px solid ${C.alert}` }}>
        <div style={{ fontSize: 14, lineHeight: 1.45 }}>Could not be read as a programme. Shown as written instead.</div>
        <div style={{ fontFamily: mono, fontSize: 11, color: C.alert, marginTop: 6, lineHeight: 1.5 }}>{reason}</div>
      </div>
    </>
  );
}

// ── a tier ────────────────────────────────────────────────────────────────

function TierScreen({
  tier, variant, onVariant, programme, cores, coresReason, movements, exercisesReason,
}: {
  tier: Tier; variant: 1 | 2; onVariant: (v: 1 | 2) => void;
  programme: Programme; cores: Cores | null; coresReason: string | null;
  movements: Movement[] | null; exercisesReason: string | null;
}) {
  const v = tier.variants.find((x) => x.n === variant) ?? tier.variants[0];
  const core = cores?.cores.find((k) => k.letter === v.core) ?? null;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0, lineHeight: 1.3 }}>{tier.name}</h2>
        <span style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, whiteSpace: "nowrap" }}>{tier.cap}</span>
      </div>
      {tier.when && <p style={{ fontSize: 13.5, color: C.inkSoft, lineHeight: 1.55, margin: "6px 0 0" }}>{tier.when}</p>}

      {/* The two variants, side by side. Which one is next is the Training
          slot's business and arrives as `target`; nothing here remembers which
          was opened last. */}
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        {tier.variants.map((x) => (
          <button
            key={x.n}
            onClick={() => onVariant(x.n as 1 | 2)}
            style={{
              flex: 1, padding: "9px 0", borderRadius: 9, cursor: "pointer", fontFamily: sans, fontSize: 13,
              border: `1px solid ${x.n === v.n ? C.ink : C.ruleStrong}`,
              background: x.n === v.n ? C.ink : "transparent",
              color: x.n === v.n ? C.ground : C.inkSoft,
            }}
          >Variant {x.n} · Core {x.core}</button>
        ))}
      </div>

      <Label>{`CORE ${v.core} · FROM THIS APP, NOT SWORKIT`}</Label>
      {core ? (
        <CoreSteps core={core} movements={movements} exercisesReason={exercisesReason} />
      ) : (
        <p style={{ fontSize: 13, color: C.alert, lineHeight: 1.5 }}>
          {coresReason ? `core.md could not be read: ${coresReason}` : `There is no Core ${v.core} in core.md.`}
        </p>
      )}

      <Label>WORK · FROM THE SWORKIT LIBRARY</Label>
      {v.rows.map((r, i) => (
        <WorkRow key={i} n={i + 1} row={r} programme={programme} movements={movements} exercisesReason={exercisesReason} />
      ))}

      {tier.notes && <div style={{ marginTop: 18 }}><Markdown text={tier.notes} /></div>}
    </>
  );
}

/** Warning glyph on a row whose Sworkit entry is the wrong movement. Visible while scanning, not only when open. */
const Warn = () => (
  <span title="the Sworkit entry is the wrong movement" style={{ fontFamily: mono, fontSize: 11, color: C.flagInk, marginLeft: 8, whiteSpace: "nowrap" }}>⚠ sworkit</span>
);

/** The unlock condition of a parked movement: the `It comes out` block, or the first block after Target. */
const unlockOf = (m: Movement) =>
  m.blocks.find((b) => /comes out/i.test(b.label))?.text ?? m.blocks.find((b) => b.label !== "Target")?.text ?? "";

function cuesOf(cues: number[], movements: Movement[] | null): Movement[] {
  if (!movements) return [];
  return cues.map((n) => movements.find((m) => m.n === n)).filter(Boolean) as Movement[];
}

/**
 * One movement in the work block: number, name, dose. Tapping expands the
 * cueing in place — it never navigates away, because losing your place in the
 * list is the failure this view exists to fix. The expanded state lives in
 * this component and nowhere else.
 */
function WorkRow({
  n, row, programme, movements, exercisesReason,
}: { n: number; row: MovementRow; programme: Programme; movements: Movement[] | null; exercisesReason: string | null }) {
  const [open, setOpen] = useState(false);
  const cued = cuesOf(row.cues, movements);
  const ladder = row.ladder ? programme.ladders.find((l) => l.name === row.ladder) ?? null : null;
  const rung = ladder && ladder.current !== null ? ladder.rungs.find((r) => r.level === ladder.current) ?? null : null;
  const parked = cued.length > 0 && cued.every((m) => m.kind === "parked");
  const warn = cued.some((m) => m.sworkit);

  return (
    <div style={{ ...card, cursor: "pointer", opacity: parked ? 0.55 : 1 }} onClick={() => setOpen((x) => !x)}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <span style={{ fontFamily: mono, fontSize: 11, color: C.inkGhost, width: 16, flexShrink: 0 }}>{n}</span>
        <span style={{ fontSize: 15, lineHeight: 1.4, flex: 1 }}>
          <Inline text={row.movement} />
          {warn && <Warn />}
        </span>
        <span style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, whiteSpace: "nowrap" }}>{row.dose}</span>
      </div>

      {parked && (
        <div style={{ fontSize: 12.5, color: C.inkSoft, lineHeight: 1.5, marginTop: 6 }}>
          Parked. Comes out: {unlockOf(cued[0])}
        </div>
      )}

      {open && (
        <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 12, cursor: "auto" }}>
          {ladder && (
            rung ? (
              <RungLine ladder={ladder} rung={rung} />
            ) : (
              <p style={{ fontSize: 13, color: C.inkSoft, lineHeight: 1.5, margin: "0 0 10px" }}>
                No rung is marked on the {ladder.name} ladder yet. The pattern is whichever rung is current in programme.md.
              </p>
            )
          )}
          {row.ladder === null && /current rung$/i.test(row.movement) && (
            <p style={{ fontSize: 13, color: C.inkSoft, lineHeight: 1.5, margin: "0 0 10px" }}>
              There is no ladder by this name in programme.md; the cueing below is what there is.
            </p>
          )}
          <Cues cued={cued} movements={movements} exercisesReason={exercisesReason} wanted={row.cues.length} />
        </div>
      )}
    </div>
  );
}

/** The current rung of a ladder, named. Where you are, and nothing about how you got there. */
function RungLine({ ladder, rung }: { ladder: Ladder; rung: Ladder["rungs"][number] }) {
  return (
    <div style={{ background: C.cardQuiet, border: `1px solid ${C.rule}`, borderRadius: 10, padding: "10px 12px", marginBottom: 10 }}>
      <div style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, letterSpacing: 1 }}>{ladder.name.toUpperCase()} · RUNG {rung.level}</div>
      <div style={{ fontSize: 14, lineHeight: 1.45, marginTop: 4 }}><Inline text={rung.movement} /></div>
      <div style={{ fontSize: 12.5, color: C.inkSoft, lineHeight: 1.5, marginTop: 4 }}>Next rung when: {rung.gate}</div>
    </div>
  );
}

/** The cueing for a row: the entries it points at, or an honest line when there are none. */
function Cues({ cued, movements, exercisesReason, wanted }: { cued: Movement[]; movements: Movement[] | null; exercisesReason: string | null; wanted: number }) {
  if (exercisesReason) {
    return <p style={{ fontSize: 13, color: C.alert, lineHeight: 1.5, margin: 0 }}>The cueing could not be read: {exercisesReason}</p>;
  }
  if (wanted === 0) {
    return <p style={{ fontSize: 13, color: C.inkSoft, lineHeight: 1.5, margin: 0 }}>No cue has been written for this movement.</p>;
  }
  return <>{cued.map((m) => <MovementCard key={m.n} m={m} movements={movements} />)}</>;
}

// ── a core ────────────────────────────────────────────────────────────────

function CoreScreen({ core, cores, movements, exercisesReason }: { core: Core; cores: Cores; movements: Movement[] | null; exercisesReason: string | null }) {
  const [rules, setRules] = useState(false);
  return (
    <>
      <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0, lineHeight: 1.3 }}>Core {core.letter} — {core.name}</h2>
      {core.intro && <div style={{ marginTop: 8 }}><Markdown text={core.intro} /></div>}

      <Label>THE STEPS · FROM THIS APP, NOT SWORKIT</Label>
      <CoreSteps core={core} movements={movements} exercisesReason={exercisesReason} />

      {core.trailing && <div style={{ marginTop: 18 }}><Markdown text={core.trailing} /></div>}

      {/* The massage gun rules and the rest of the page's preamble. Folded,
          because they are read once; present, because nothing is hidden. */}
      {cores.preamble && (
        <>
          <Label>BEFORE EITHER</Label>
          <div onClick={() => setRules((x) => !x)} style={card}>
            <div style={{ fontSize: 14, color: C.inkSoft }}>{rules ? "The massage gun rules and the rest of the page" : "The massage gun rules — read once"}</div>
            {rules && <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 12, cursor: "auto" }}><Markdown text={cores.preamble} /></div>}
          </div>
        </>
      )}
    </>
  );
}

function CoreSteps({ core, movements, exercisesReason }: { core: Core; movements: Movement[] | null; exercisesReason: string | null }) {
  return <>{core.steps.map((s) => <CoreStepRow key={s.n} step={s} movements={movements} exercisesReason={exercisesReason} />)}</>;
}

function CoreStepRow({ step, movements, exercisesReason }: { step: Core["steps"][number]; movements: Movement[] | null; exercisesReason: string | null }) {
  const [open, setOpen] = useState(false);
  const cued = cuesOf(step.cues, movements);
  const warn = cued.some((m) => m.sworkit);
  return (
    <div style={card} onClick={() => setOpen((x) => !x)}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <span style={{ fontFamily: mono, fontSize: 11, color: C.inkGhost, width: 16, flexShrink: 0 }}>{step.n}</span>
        <span style={{ fontSize: 15, lineHeight: 1.4, flex: 1 }}><Inline text={step.movement} />{warn && <Warn />}</span>
        <span style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, whiteSpace: "nowrap" }}>{step.time}</span>
      </div>
      {open && (
        <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 10, cursor: "auto" }}>
          <p style={{ fontSize: 14, color: C.inkSoft, lineHeight: 1.6, margin: "0 0 10px" }}><Inline text={step.text} /></p>
          {step.cues.length > 0 && <Cues cued={cued} movements={movements} exercisesReason={exercisesReason} wanted={step.cues.length} />}
        </div>
      )}
    </div>
  );
}

// ── a ladder ──────────────────────────────────────────────────────────────

function LadderScreen({ ladder, movements, exercisesReason }: { ladder: Ladder; movements: Movement[] | null; exercisesReason: string | null }) {
  return (
    <>
      <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0, lineHeight: 1.3 }}>{ladder.name}</h2>
      <p style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, margin: "6px 0 0" }}>{ladder.where}</p>

      {/* Where you are, or that nothing is marked. Never since when. */}
      {ladder.current === null && (
        <p style={{ fontSize: 13, color: C.inkSoft, lineHeight: 1.55, margin: "12px 0 0" }}>
          No rung is marked as current yet. When you know which one you are on, tell Claude and it goes into programme.md.
        </p>
      )}

      <Label>THE RUNGS</Label>
      {ladder.rungs.map((r) => <RungRow key={r.level} ladder={ladder} rung={r} movements={movements} exercisesReason={exercisesReason} />)}

      {ladder.note && <div style={{ marginTop: 18 }}><Markdown text={ladder.note} /></div>}
    </>
  );
}

/**
 * One rung. The current one carries a marker and a stronger border. The rungs
 * below it are not ticked, dimmed or struck through, and the ones above are
 * not locked or greyed: the only rung that looks different is the one you
 * are on. A parked movement is the one exception — greyed with its unlock
 * condition, because that is about the movement, not about him.
 */
function RungRow({ ladder, rung, movements, exercisesReason }: { ladder: Ladder; rung: Ladder["rungs"][number]; movements: Movement[] | null; exercisesReason: string | null }) {
  const [open, setOpen] = useState(false);
  const here = ladder.current === rung.level;
  const cued = cuesOf(rung.cues, movements);
  const parked = cued.length > 0 && cued.every((m) => m.kind === "parked");
  const warn = cued.some((m) => m.sworkit);
  return (
    <div
      onClick={() => setOpen((x) => !x)}
      style={{ ...card, opacity: parked ? 0.55 : 1, border: `1px solid ${here ? C.ink : C.rule}` }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <span style={{ fontFamily: mono, fontSize: 11, color: here ? C.ink : C.inkGhost, width: 16, flexShrink: 0 }}>{rung.level}</span>
        <span style={{ fontSize: 15, lineHeight: 1.4, flex: 1 }}><Inline text={rung.movement} />{warn && <Warn />}</span>
        {here && <span style={{ fontFamily: mono, fontSize: 11, color: C.ink, letterSpacing: 1, whiteSpace: "nowrap" }}>◂ HERE</span>}
      </div>
      <div style={{ fontSize: 12.5, color: C.inkSoft, lineHeight: 1.5, marginTop: 5, paddingLeft: 26 }}>
        Next rung when: {rung.gate}
      </div>
      {parked && (
        <div style={{ fontSize: 12.5, color: C.inkSoft, lineHeight: 1.5, marginTop: 5, paddingLeft: 26 }}>
          Parked. Comes out: {unlockOf(cued[0])}
        </div>
      )}
      {open && (
        <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 12, cursor: "auto" }}>
          <Cues cued={cued} movements={movements} exercisesReason={exercisesReason} wanted={rung.cues.length} />
        </div>
      )}
    </div>
  );
}

// ── the movements ─────────────────────────────────────────────────────────

function MovementList({ movements, preamble }: { movements: Movement[]; preamble: string }) {
  const [about, setAbout] = useState(false);
  let section = "";
  return (
    <>
      <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0, lineHeight: 1.3 }}>Every movement</h2>
      {preamble && (
        <div onClick={() => setAbout((x) => !x)} style={{ ...card, marginTop: 12 }}>
          <div style={{ fontSize: 14, color: C.inkSoft }}>{about ? "How to use the cueing, in full" : "How to use the cueing"}</div>
          {about && <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 12, cursor: "auto" }}><Markdown text={preamble} /></div>}
        </div>
      )}
      {movements.map((m) => {
        const heading = m.section !== section ? (section = m.section) : null;
        return (
          <div key={m.n}>
            {heading && <Label>{heading.toUpperCase()}</Label>}
            <MovementRowCard m={m} movements={movements} />
          </div>
        );
      })}
    </>
  );
}

function SearchResults({ q, movements }: { q: string; movements: Movement[] }) {
  const hits = useMemo(() => movements.filter((m) => {
    const hay = [m.name, m.section, m.note ?? "", m.prose, ...m.blocks.map((b) => `${b.label} ${b.text}`)].join("\n").toLowerCase();
    return hay.includes(q);
  }), [q, movements]);
  if (!hits.length) return <p style={{ fontSize: 13, color: C.inkFaint, marginTop: 14 }}>Nothing in the cueing matches that.</p>;
  return (
    <div style={{ marginTop: 10 }}>
      {hits.map((m) => <MovementRowCard key={m.n} m={m} movements={movements} />)}
    </div>
  );
}

/** A movement as a row that opens in place. */
function MovementRowCard({ m, movements }: { m: Movement; movements: Movement[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div onClick={() => setOpen((x) => !x)} style={{ ...card, opacity: m.kind === "parked" ? 0.55 : 1 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <span style={{ fontFamily: mono, fontSize: 11, color: C.inkGhost, width: 20, flexShrink: 0 }}>{m.n}</span>
        <span style={{ fontSize: 15, lineHeight: 1.4, flex: 1 }}>
          <Inline text={m.name} />
          {m.sworkit && <Warn />}
          {m.kind === "parked" && <span style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, marginLeft: 8 }}>parked</span>}
        </span>
      </div>
      {open && <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 10, cursor: "auto" }}><MovementBody m={m} movements={movements} /></div>}
    </div>
  );
}

/** A movement's cueing as a framed card, used inside an expanded row. */
function MovementCard({ m, movements }: { m: Movement; movements: Movement[] | null }) {
  return (
    <div style={{ background: C.cardQuiet, border: `1px solid ${C.rule}`, borderRadius: 10, padding: "10px 12px", marginBottom: 10 }}>
      <div style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, letterSpacing: 1 }}>
        {m.n} · {m.kind === "parked" ? "PARKED" : "THE CUEING"}
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.45, marginTop: 4 }}><Inline text={m.name} /></div>
      <div style={{ marginTop: 8 }}><MovementBody m={m} movements={movements} /></div>
    </div>
  );
}

/**
 * The five blocks. The plain-language location leads, in the reading size,
 * and the target — common and anatomical names — sits under it as a small
 * secondary label. That order is the brief's and it is not to be swapped:
 * he does not know muscles by name, so the Latin is a footnote, not a heading.
 */
function MovementBody({ m, movements }: { m: Movement; movements: Movement[] | null }) {
  if (m.kind === "reference") {
    const target = movements?.find((x) => x.n === m.ref) ?? null;
    return (
      <>
        <p style={{ fontSize: 14, color: C.inkSoft, lineHeight: 1.6, margin: "0 0 10px" }}><Inline text={m.prose} /></p>
        {target && <MovementCard m={target} movements={movements} />}
      </>
    );
  }
  const block = (label: string) => m.blocks.find((b) => b.label === label) ?? null;
  const where = block("Where it is");
  const target = block("Target");
  const rest = m.blocks.filter((b) => b !== where && b !== target && b.label !== "Sworkit");
  return (
    <>
      {m.sworkit && (
        <div style={{ background: C.flagBg, color: C.flagInk, borderRadius: 8, padding: "8px 10px", fontSize: 12.5, lineHeight: 1.5, marginBottom: 10 }}>
          <Inline text={m.sworkit} />
        </div>
      )}
      {m.note && <p style={{ fontSize: 13, color: C.inkFaint, lineHeight: 1.55, margin: "0 0 10px", fontStyle: "italic" }}><Inline text={m.note} /></p>}
      {where && (
        <>
          <div style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, letterSpacing: 1 }}>WHERE IT IS</div>
          <p style={{ fontSize: 14.5, color: C.ink, lineHeight: 1.6, margin: "2px 0 4px" }}><Inline text={where.text} /></p>
        </>
      )}
      {target && <p style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, lineHeight: 1.5, margin: "0 0 12px" }}><Inline text={target.text} /></p>}
      {rest.map((b) => (
        <div key={b.label} style={{ marginBottom: 10 }}>
          <div style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, letterSpacing: 1 }}>{b.label.toUpperCase()}</div>
          <p style={{ fontSize: 14, color: C.inkSoft, lineHeight: 1.6, margin: "2px 0 0" }}><Inline text={b.text} /></p>
        </div>
      ))}
    </>
  );
}

/** One line of a document — bold, italics, code — inside the row's own layout rather than a paragraph of its own. */
function Inline({ text }: { text: string }) {
  return <>{inline(text, text.slice(0, 12))}</>;
}
